// @ts-strict-ignore
import Tabs from 'antd/lib/tabs';
import * as React from 'react';
import { StabilityIssue } from '../../types';
import Text from 'antd/lib/typography/Text';
import { antGrey, runwayColors, titleStyle } from '../styles/globalStyles';
import List from 'antd/lib/list';
import Space from 'antd/lib/space';
import { InfoCircleOutlined } from '@ant-design/icons';
import StabilityIssueItem from './StabilityIssueItem';
import { Destination } from '@/types/destinations';
import { DestinationCounter } from '@/components/appHome/DestinationCounter';

interface StabilityIssuesProps {
    topStabilityIssues: StabilityIssue[];
    topNewStabilityIssues: StabilityIssue[];
    topStabilityIssuesNonFatal: StabilityIssue[];
    topNewStabilityIssuesNonFatal: StabilityIssue[];
    backgroundColor: string;
    shouldShowNonFatals: boolean;
    onShowDrawerForIssue?: (issue: StabilityIssue) => void;
    destinationsByStepConfigId?: Record<string, Destination>;
}

const StabilityIssues: React.FC<StabilityIssuesProps> = (props: StabilityIssuesProps) => {
    const [activeTabKey, setActiveTabKey] = React.useState('new');

    const renderItem = (issue: StabilityIssue, showNew: boolean, showTotal: boolean) => {
        const destination = props.destinationsByStepConfigId?.[issue.destinationStepConfigId];
        return (
            <List.Item
                key={`${issue.destinationStepConfigId ?? ''}-${issue.issueID}`}
                style={{ paddingTop: 6, paddingBottom: 6 }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', minWidth: 0 }}>
                    {destination && <DestinationCounter destinations={[destination]} size={18} />}
                    <StabilityIssueItem
                        issue={issue}
                        showNew={showNew}
                        showTotal={showTotal}
                        onShowIssueDrawer={props.onShowDrawerForIssue}
                    />
                </div>
            </List.Item>
        );
    };

    const tabPaneTextStyle: React.CSSProperties = {
        color: runwayColors.primary,

        textAlign: 'center',
    };

    const tabPanelTextContainerStyle = (isNonFatal: boolean) => {
        return {
            paddingLeft: 8,
            paddingRight: 8,
            width: isNonFatal ? 110 : 55,
            display: 'flex',
            justifyContent: 'center',
        };
    };

    return (
        <>
            <Tabs
                tabBarExtraContent={{
                    right: (
                        <Space size={16}>
                            <Text style={{ ...titleStyle, fontSize: 10, fontWeight: 400 }}>
                                In release
                                {activeTabKey === 'all' || activeTabKey === 'all-non-fatal' ? ' / total ' : null}
                            </Text>
                            <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                        </Space>
                    ),
                }}
                activeKey={activeTabKey}
                style={{
                    backgroundColor: props.backgroundColor,
                    borderRadius: 8,
                    paddingTop: 8,
                    paddingBottom: 8,
                    paddingLeft: 16,
                    paddingRight: 16,
                }}
                size="small"
                className="stab-issues-tabs"
                onTabClick={(activeKey) => setActiveTabKey(activeKey)}
            >
                <Tabs.TabPane
                    key="new"
                    tab={
                        <div style={tabPanelTextContainerStyle(false)}>
                            <Text style={tabPaneTextStyle}>New</Text>
                        </div>
                    }
                >
                    <List
                        renderItem={(item) => renderItem(item, false, false)}
                        dataSource={props.topNewStabilityIssues}
                        split
                        className="stab-issues-list"
                    >
                        {props.topNewStabilityIssues?.length === 0 && <Text type="secondary">No new crashes</Text>}
                    </List>
                </Tabs.TabPane>
                <Tabs.TabPane
                    key="all"
                    tab={
                        <div style={tabPanelTextContainerStyle(false)}>
                            <Text style={tabPaneTextStyle}>All</Text>
                        </div>
                    }
                >
                    <List
                        renderItem={(item) => renderItem(item, true, true)}
                        dataSource={props.topStabilityIssues}
                        split={true}
                        className="stab-issues-list"
                    >
                        {props.topStabilityIssues?.length === 0 && <Text type="secondary">No crashes</Text>}
                    </List>
                </Tabs.TabPane>
                {props.shouldShowNonFatals && (
                    <Tabs.TabPane
                        key="new-non-fatal"
                        tab={
                            <div style={tabPanelTextContainerStyle(true)}>
                                <Text style={tabPaneTextStyle}>New (non-fatal)</Text>
                            </div>
                        }
                    >
                        <List
                            renderItem={(item) => renderItem(item, false, false)}
                            dataSource={props.topNewStabilityIssuesNonFatal}
                            split
                            className="stab-issues-list"
                        >
                            {props.topNewStabilityIssuesNonFatal?.length === 0 && (
                                <Text type="secondary">No new events</Text>
                            )}
                        </List>
                    </Tabs.TabPane>
                )}
                {props.shouldShowNonFatals && (
                    <Tabs.TabPane
                        key="all-non-fatal"
                        tab={
                            <div style={tabPanelTextContainerStyle(true)}>
                                <Text style={tabPaneTextStyle}>All (non-fatal)</Text>
                            </div>
                        }
                    >
                        <List
                            renderItem={(item) => renderItem(item, true, true)}
                            dataSource={props.topStabilityIssuesNonFatal}
                            split={true}
                            className="stab-issues-list"
                        >
                            {props.topStabilityIssuesNonFatal?.length === 0 && <Text type="secondary">No events</Text>}
                        </List>
                    </Tabs.TabPane>
                )}
            </Tabs>
        </>
    );
};

export default StabilityIssues;
